import { useState } from 'react';

import { CallToActionKind } from '@/types';
import { DESIGN_SYSTEM_SIZES } from '@/constants/styles';
import { URLS } from '@/constants/urls';
import { getPushWebp } from '@/utils/espotUtils';

import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import Icon from '@/components/common/iconedLinks/Icon';
import CenteredModal from '@/components/layout/modals/CenteredModal';

import styles from './PushBento.module.scss';
import { useTranslation } from 'next-i18next';

export type PushBentoProps = {
  url: string;
  img: string;
  alt: string;
  contentTitle: string;
  desc: string;
  label?: string;
  informations?: string;
  hasButton: boolean;
  hasArrowIcon: boolean;
  hasOverlay: boolean;
  size?: 'small' | 'large';
};

export const PushBento = ({
  url, img, alt, contentTitle, desc, label,
  informations, hasButton, hasArrowIcon, hasOverlay, size = 'small'
}: PushBentoProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleClick = () => {
    if (url && typeof window !== 'undefined') {
      window.location.href = url;
    }
  };

  const containerClassName = [
    styles.container,
    url && styles.hoverable,
    hasOverlay && styles.overlay
  ].filter(Boolean).join(' ');

  const informationsClassName = [
    styles.informations,
    size === 'large' && styles.large,
  ].filter(Boolean).join(' ');

  const titleClassName = [
    styles.title,
    size === 'large' && styles.large,
  ].filter(Boolean).join(' ');

  return (
    <>
      <div className={containerClassName} onClick={handleClick} >
        <picture className={styles.image}>
          <source srcSet={getPushWebp(img)} type="image/webp" />
          <img
            src={img}
            alt={alt}
            className={styles.img}
            loading="eager"
            fetchPriority="high"
            data-test-id="background-image"
          />
        </picture>

        <div className={informationsClassName}>
          <div className={styles.content}>
            <div
              dangerouslySetInnerHTML={{ __html: contentTitle }}
              className={titleClassName}
            />
            {desc && (
              <div
                dangerouslySetInnerHTML={{ __html: desc }}
                className={styles.description}
              />
            )}
            {hasButton && (
              <CallToActionContainer
                kind={CallToActionKind.REVERSE_PRIMARY}
                className={styles.button}
                buttonSize={DESIGN_SYSTEM_SIZES.LARGE}
              >
                {t(`common:btn.${label.toLowerCase()}`)}
              </CallToActionContainer>
            )}
          </div>

          {informations && (
            <Icon
              src={URLS.INFORMATION_SOLID_WHITE}
              alt={t('alter:informations')}
              width={24}
              height={24}
              className={styles['information-icon']}
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(true);
              }}
            />
          )}
          {hasArrowIcon && (
            <Icon
              src={URLS.ANGLE_RIGHT_WHITE}
              alt={t('alter:angleRightIcon')}
              width={24}
              height={24}
            />
          )}
        </div>
      </div>

      {informations && (
        <CenteredModal
          opened={isModalOpen}
          title={t('common:product.conditions')}
          customContent={informations}
          cancelAction={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default PushBento;
